import { makeAutoObservable, action } from "mobx";

import { Variable } from "./types";

class InputVariableConfiguratorStore {
  private _inputVariables: Variable[] = [];
  private _searchQuery = "";
  private _name = "";

  constructor(initialVariables: Variable[] = [], initialName = "") {
    this._inputVariables = initialVariables;
    this._name = initialName;
    makeAutoObservable(this);
  }

  @action setSearchQuery(query: string) {
    this._searchQuery = query;
  }

  @action clearSearchQuery() {
    this._searchQuery = "";
  }

  @action setName(name: string) {
    this._name = name;
  }

  @action addVariable(key?: string, value?: string | object) {
    const variableKey = key || `variable${this._inputVariables.length + 1}`;
    const variableValue = value ?? "";

    const newVariable: Variable = {
      id: Math.random().toString(36).substring(7),
      key: variableKey,
      value: variableValue,
    };

    this._inputVariables = [...this._inputVariables, newVariable];
  }

  @action updateVariable(id: string, key: string, value: string | object) {
    this._inputVariables = this._inputVariables.map(v => (v.id === id ? { ...v, key, value } : v));
  }

  @action removeVariable(id: string) {
    this._inputVariables = this._inputVariables.filter(v => v.id !== id);
  }

  @action clearAll() {
    this._inputVariables = [];
  }

  @action importVariables(newVariables: Record<string, string>) {
    const existingKeys = new Set(this._inputVariables.map(v => v.key));

    Object.entries(newVariables).forEach(([key, value]) => {
      if (existingKeys.has(key)) {
        const existingVariable = this._inputVariables.find(v => v.key === key);
        if (existingVariable) {
          this.updateVariable(existingVariable.id, key, this._getVariableValue(value));
        }
      } else {
        this.addVariable(key, this._getVariableValue(value));
      }
    });
  }

  private _getVariableValue(value?: string | object | null | number | boolean): string | object {
    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }
    return String(value ?? "");
  }

  get inputVariables() {
    return this._inputVariables;
  }

  get searchQuery() {
    return this._searchQuery;
  }

  get filteredVariables() {
    if (!this._searchQuery.trim()) {
      return this._inputVariables;
    }

    const query = this._searchQuery.toLowerCase();
    return this._inputVariables.filter(variable => {
      const keyMatch = variable.key.toLowerCase().includes(query);
      const valueString =
        typeof variable.value === "string" ? variable.value : JSON.stringify(variable.value);
      const valueMatch = valueString.toLowerCase().includes(query);
      return keyMatch || valueMatch;
    });
  }

  get hasSearchResults() {
    return this.filteredVariables.length > 0;
  }

  get isSearching() {
    return this._searchQuery.trim().length > 0;
  }

  get variableCount() {
    return this._inputVariables.length;
  }

  get filteredVariableCount() {
    return this.filteredVariables.length;
  }

  get name() {
    return this._name;
  }
}

export default InputVariableConfiguratorStore;
